import React, { useState, useEffect } from 'react';
import { getActiveGalleryImages } from '../api/galleryApi';

const Media = () => {
  const [galleryImages, setGalleryImages] = useState([]);
  const [galleryVideos, setGalleryVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Base URL for server-hosted assets
  const API_BASE = process.env.REACT_APP_API_URL || '';

  useEffect(() => {
    const fetchGalleryMedia = async () => {
      try {
        setLoading(true);
        const response = await getActiveGalleryImages();
        
        // Separate images and videos based on mediaType
        const images = [];
        const videos = [];

        if (Array.isArray(response)) {
          response.forEach(item => {
            if (item.mediaType === 'photo') {
              images.push({
                id: item._id,
                url: item.mediaUrl.startsWith('http') ? item.mediaUrl : `${API_BASE}${item.mediaUrl}`,
                caption: item.title,
                alt: item.altText || item.title
              });
            } else if (item.mediaType === 'video') {
              // Extract YouTube video ID from URL
              let videoId = item.mediaUrl;
              if (item.mediaUrl.includes('youtube.com') || item.mediaUrl.includes('youtu.be')) {
                const match = item.mediaUrl.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
                videoId = match ? match[1] : item.mediaUrl;
              }
              videos.push({
                id: item._id,
                videoId: videoId,
                title: item.title,
                description: item.description || ''
              });
            }
          });
        }

        setGalleryImages(images);
        setGalleryVideos(videos);
        setError(null);
      } catch (err) {
        console.error('Error fetching gallery media:', err);
        setError('Failed to load gallery. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchGalleryMedia();
  }, [API_BASE]);

  if (loading) {
    return (
      <div style={{ padding: "20px", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ 
            width: "48px", 
            height: "48px", 
            border: "4px solid #f3f3f3", 
            borderTop: "4px solid #672f89", 
            borderRadius: "50%", 
            animation: "spin 1s linear infinite",
            margin: "0 auto"
          }}></div>
          <p style={{ marginTop: "20px", color: "#666" }}>Loading gallery...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: "20px", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center", color: "#ef4444" }}>
          <p style={{ fontSize: "18px" }}>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @media (max-width: 768px) {
          .media-section-title {
            font-size: 30px !important;
            margin-bottom: 12px !important;
          }
          .media-section-subtitle {
            font-size: 14px !important;
          }
        }
      `}</style>
      <div style={{ padding: "20px 20px", backgroundColor: "#fff", minHeight: "100vh" }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
          {/* Image Gallery Section */}
          {galleryImages.length > 0 && (
            <>
              {/* Header Section */}
              <div style={{ textAlign: "center", marginBottom: "60px" }}>
                <h2 className="media-section-title" style={{ fontSize: "46px", fontWeight: "600", marginBottom: "24px", color: "#000000", letterSpacing: "-0.5px" }}>
                  Image Gallery
                </h2>
                <p className="media-section-subtitle" style={{ maxWidth: "850px", margin: "0 auto", color: "#666", lineHeight: "1.8", fontSize: "18px", fontWeight: "400" }}>
                  Capturing moments of transformation and community engagement across India.
                </p>
              </div>

              {/* Images Grid */}
              <div style={{ 
                display: "grid", 
                gridTemplateColumns: "repeat(auto-fill, minmax(350px, 1fr))", 
                gap: "30px" 
              }}>
                {galleryImages.map((image) => (
                  <div
                    key={image.id}
                    style={{
                      position: "relative",
                      backgroundColor: "#fff",
                      borderRadius: "16px",
                      overflow: "hidden",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                      border: "1px solid #eee",
                      transition: "all 0.3s ease"
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-8px)";
                      e.currentTarget.style.boxShadow = "0 12px 24px rgba(0,0,0,0.15)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)";
                    }}
                  >
                    <div style={{ height: "280px", position: "relative", overflow: "hidden" }}>
                      <img
                        src={image.url}
                        alt={image.alt}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          transition: "transform 0.3s ease"
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.transform = "scale(1.05)";
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.transform = "scale(1)";
                        }}
                        onError={(e) => {
                          e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="300" height="300"%3E%3Crect width="100%25" height="100%25" fill="%23e5e7eb"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" fill="%236b7280" font-family="Arial" font-size="20"%3EImage Not Available%3C/text%3E%3C/svg%3E';
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Video Gallery Section */}
          {galleryVideos.length > 0 && (
            <div style={{ marginTop: galleryImages.length > 0 ? "100px" : "0" }}>
              {/* Video Header */}
              <div style={{ textAlign: "center", marginBottom: "60px" }}>
                <h2 className="media-section-title" style={{ fontSize: "46px", fontWeight: "600", marginBottom: "24px", color: "#000000", letterSpacing: "-0.5px" }}>
                  Video Gallery
                </h2>
                <p className="media-section-subtitle" style={{ maxWidth: "850px", margin: "0 auto", color: "#666", lineHeight: "1.8", fontSize: "18px", fontWeight: "400" }}>
                  Watch our latest videos and updates from various events and programs.
                </p>
              </div>

              {/* Videos Grid */}
              <div style={{ 
                display: "grid", 
                gridTemplateColumns: "repeat(auto-fill, minmax(350px, 1fr))", 
                gap: "30px" 
              }}>
                {galleryVideos.map((video) => (
                  <div
                    key={video.id}
                    style={{
                      position: "relative",
                      backgroundColor: "#fff",
                      borderRadius: "16px",
                      overflow: "hidden",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                      border: "1px solid #eee",
                      transition: "all 0.3s ease"
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-8px)";
                      e.currentTarget.style.boxShadow = "0 12px 24px rgba(0,0,0,0.15)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)";
                    }}
                  >
                    <div style={{ position: "relative", paddingBottom: "56.25%", height: 0, overflow: "hidden" }}>
                      <iframe
                        src={`https://www.youtube.com/embed/${video.videoId}`}
                        title={video.title}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%"
                        }}
                      ></iframe>
                    </div>
                    {(video.title || video.description) && (
                      <div style={{ padding: "20px" }}>
                        {video.title && (
                          <h3 style={{ fontSize: "18px", fontWeight: "600", color: "#000", marginBottom: "8px" }}>
                            {video.title}
                          </h3>
                        )}
                        {video.description && (
                          <p style={{ fontSize: "14px", color: "#666", lineHeight: "1.6" }}>
                            {video.description}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Empty State */}
          {galleryImages.length === 0 && galleryVideos.length === 0 && (
            <div style={{ textAlign: "center", paddingTop: "100px", paddingBottom: "100px" }}>
              <div style={{ 
                width: "120px", 
                height: "120px", 
                backgroundColor: "#f3f4f6", 
                borderRadius: "50%", 
                display: "inline-flex", 
                alignItems: "center", 
                justifyContent: "center",
                marginBottom: "30px"
              }}>
                <svg style={{ width: "60px", height: "60px", color: "#9ca3af" }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <h2 style={{ fontSize: "32px", fontWeight: "600", color: "#374151", marginBottom: "12px" }}>No media available</h2>
              <p style={{ fontSize: "18px", color: "#6b7280" }}>Check back later for photos and videos.</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Media;